import React, { useEffect } from "react";
import AlongLogo from "components/AlongLogo";
import OAuthButton from "./OAuthButton";
import { featureEnabled } from "utils/env";
import {
  ActionType,
  EventName,
  SurfaceType,
} from "utils/analytics/analyticsConstants";
import {
  AnalyticsProvider,
  useAnalytics,
} from "utils/analytics/AnalyticsContext";
import Stack from "ui/Stack";
import Typography from "ui/Typography";
import DevLogin from "./DevLogin";
import UserLoginModal from "./UserLoginModals/index";
import { LiveChat } from "components/LiveChat/LiveChat";
import Alert from "ui/Alert";
import { ErrorMessages } from "pages/UserLogin/UserLoginModals";
import NoPII from "utils/NoPII";
import { useLocation } from "react-router-dom";
import styled from "styled-components/macro";
import { theme } from "ui/theme";
import { TeacherAccount } from "ui/Icons";
import Link from "ui/Link";
import PageRipSVG from "images/page-rip.svg";
import GuestLayout from "components/GuestLayout";

function shouldEnableLiveChatWidget(loginError: string) {
  return (
    loginError === ErrorMessages.PENDING_ACCOUNT_LIVE_SUPPORT ||
    loginError === ErrorMessages.PENDING_ACCOUNT
  );
}

// There are several LegacyAuthError error messages that don't map to ErrorMessages enum.
// We show them as a red banner on top of the page directly.
function shouldShowAlertModal(loginError: string) {
  const arr: Array<string> = Object.values(ErrorMessages);
  return loginError && !arr.includes(loginError);
}

const $LinkWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  padding-top: 0.5rem;
  svg {
    margin-right: 0.5rem;
  }
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
`;

const $CreateAccountFooter = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${theme.ColorSneaker100};
  position: relative;
  margin-top: 4rem;
  padding: 1rem;
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;

  &::before {
    display: block;
    content: "";
    position: absolute;
    height: 3.5rem;
    top: -3rem;
    left: 0;
    right: 0;
    background-image: url(${PageRipSVG});
    background-repeat: repeat-x;
    z-index: 1;
  }
`;

const $TextContainer = styled.div`
  padding: 0 1rem;
`;

const $EducatorSignupPageHeaderText = styled(Typography).attrs((props) => ({
  size: props.size || "h2",
}))`
  width: 18.5rem;
  padding: ${({ theme }) => theme.SizeXs} 0;
`;

// Needs an important flag here to override the margin coming from parent <Stack/>
const $LoginBtnContainer = styled(Stack).attrs({
  $align: "center",
  $space: "SizeMd",
})`
  margin-bottom: 1.9rem !important;
`;

export default function UserSignIn() {
  const location = useLocation();
  const [loginError, setLoginError] = React.useState("");
  const enableDevLogin = featureEnabled("ENABLE_DEV_LOGIN_ROUTES");
  const { trackEvent } = useAnalytics("user_signin");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const loginErrorMessage = params.get("error") || "";
    setLoginError(loginErrorMessage);
    if (loginErrorMessage) {
      // clear the query param to dismiss the error
      window.history.pushState("clear", "", "?#");
      // Track event that alert is displayed
      trackEvent({
        eventName: EventName.UserLogin.VIEWED_LOGIN_ERROR,
        eventType: ActionType.ERROR,
        analyticsSurface: SurfaceType.USER_LOGIN,
        extraProperties: {
          extraMetadata: {
            errorText: NoPII(loginErrorMessage, { allowWhitespace: true }),
          },
        },
      });
    }
  }, [location, trackEvent]);

  return (
    <AnalyticsProvider surface={SurfaceType.USER_LOGIN}>
      <GuestLayout userType="teacher">
        {shouldShowAlertModal(loginError) && (
          <Alert variant="danger">{loginError}</Alert>
        )}
        <Stack $align="center" $space="SizeXl">
          <AlongLogo variant="along-2" />
          <$EducatorSignupPageHeaderText size="h2" fontWeight="normal">
            Sign in
          </$EducatorSignupPageHeaderText>
          <$TextContainer>
            <Typography size="bodySansSerif">
              Remember to use your school email to sign in:
            </Typography>
          </$TextContainer>
          <$LoginBtnContainer>
            <UserLoginModal type={loginError} />
            <OAuthButton type="Google" />
            <OAuthButton type="Microsoft" />
          </$LoginBtnContainer>
        </Stack>

        {enableDevLogin && (
          <DevLogin isOnDarkBackground={false} isEducatorSignup={false} />
        )}

        {shouldEnableLiveChatWidget(loginError) && (
          <LiveChat
            errorMessage={
              loginError === "pending_account"
                ? ErrorMessages.PENDING_ACCOUNT
                : ErrorMessages.PENDING_ACCOUNT_LIVE_SUPPORT
            }
          />
        )}
        <$CreateAccountFooter>
          <Typography size="bodySansSerif" fontWeight="bold">
            Don’t have an account?
          </Typography>
          <$LinkWrapper>
            <TeacherAccount role="img" size="sm" title="account profile" />
            <Link href="/create_account" analyticsName="clicked_create_account">
              Create an account
            </Link>
          </$LinkWrapper>
        </$CreateAccountFooter>
      </GuestLayout>
    </AnalyticsProvider>
  );
}
